/**
 * Today's hero board, drawn imperatively (it animates every ~70ms; React stays out of
 * the loop). A port of docs/mockups/landing.html's `Board` and option B's
 * `attractEngine`, built from a `BoardStructure` only: it never has a letter to show.
 */
import type { BoardStructure } from '../../app/structure.ts';
import { answerMid, crossingChain } from '../../app/structure.ts';
import { routeStyle } from '../BoardMap.tsx';
import { ARROW_ORIGIN, ARROW_SHAPES } from '../BoardOverlay.tsx';

const U = 100;
/** The hero never prints bigger than the game's own biggest cell (--cell-max). */
const HERO_CELL_MAX = 60;
const AZ = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const SVG = 'http://www.w3.org/2000/svg';
const WEDGE = 'M2 9.2h10.6V3.6L22 12l-9.4 8.4v-5.6H2z';

const rnd = (n: number) => Math.floor(Math.random() * n);
/** A random sort, never `not`: the ghost tries letters, it never knows any. */
export const randomLetter = (not?: string): string => {
	let c: string;
	do c = AZ[rnd(26)]!;
	while (c === not);
	return c;
};

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string): HTMLElementTagNameMap[K] {
	const e = document.createElement(tag);
	if (cls) e.className = cls;
	return e;
}

/** Timers that all stop together (an engine's lifetime). */
export class Clock {
	private ids = new Set<number>();
	private frames = new Set<number>();
	private dead = false;
	later(fn: () => void, ms: number): void {
		if (this.dead) return;
		const id = window.setTimeout(() => {
			this.ids.delete(id);
			if (!this.dead) fn();
		}, ms);
		this.ids.add(id);
	}
	frame(fn: () => void): void {
		if (this.dead) return;
		if (typeof requestAnimationFrame !== 'function') return this.later(fn, 16);
		const id = requestAnimationFrame(() => {
			this.frames.delete(id);
			if (!this.dead) fn();
		});
		this.frames.add(id);
	}
	stop(): void {
		this.dead = true;
		for (const id of this.ids) window.clearTimeout(id);
		if (typeof cancelAnimationFrame === 'function') for (const id of this.frames) cancelAnimationFrame(id);
		this.ids.clear();
		this.frames.clear();
	}
}

export class HeroView {
	readonly el: HTMLDivElement;
	readonly fx: HTMLDivElement;
	private readonly cells: HTMLDivElement[] = [];
	private readonly glyphs = new Map<number, HTMLSpanElement>();
	private readonly arrows = new Map<string, SVGPathElement>();
	private readonly halves = new Map<string, HTMLDivElement>();
	private readonly pressed: ReadonlySet<number>;
	readonly s: BoardStructure;
	cs = 0;

	constructor(s: BoardStructure, label: string) {
		this.s = s;
		this.pressed = new Set(s.pressed);
		const b = (this.el = el('div', 'hb bare'));
		b.style.setProperty('--cols', String(s.width));
		b.style.setProperty('--rows', String(s.height));
		b.setAttribute('role', 'img');
		b.setAttribute('aria-label', label);
		b.dataset.testid = 'hero-board';
		const grid = el('div', 'hb-grid');
		const byCell = new Map<number, BoardStructure['clues']>();
		for (const c of s.clues) byCell.set(c.cell, [...(byCell.get(c.cell) ?? []), c]);
		let ord = 0;
		for (let r = 0; r < s.height; r++) {
			for (let c = 0; c < s.width; c++) {
				const i = r * s.width + c;
				const kind = s.kinds[i];
				let e: HTMLDivElement;
				if (kind === 'letter') {
					e = el('div', 'hb-cell L');
					e.append(el('i', 'hb-tint'));
					const g = el('span', 'hb-g');
					e.append(g);
					this.glyphs.set(i, g);
					if (this.pressed.has(i)) e.classList.add('pressed');
				} else if (kind === 'clue') {
					e = el('div', 'hb-cell C');
					for (const cl of (byCell.get(i) ?? []).sort((a, b2) => a.slot - b2.slot)) {
						const half = el('div', 'hb-cl');
						half.append(el('i', 'hb-sel'));
						half.dataset.a = cl.answerId;
						this.halves.set(cl.answerId, half);
						e.append(half);
					}
				} else {
					e = el('div', 'hb-cell B');
					if (i === 0) {
						const svg = document.createElementNS(SVG, 'svg');
						svg.setAttribute('viewBox', '0 0 24 24');
						svg.setAttribute('class', 'hb-cm');
						svg.setAttribute('aria-hidden', 'true');
						const p = document.createElementNS(SVG, 'path');
						p.setAttribute('d', WEDGE);
						p.setAttribute('transform', 'rotate(45 12 12)');
						p.setAttribute('fill', 'currentColor');
						svg.append(p);
						e.append(svg);
					}
				}
				if (kind !== 'letter') e.style.setProperty('--sd', `${ord++ * 16}ms`);
				this.cells.push(e);
				grid.append(e);
			}
		}
		// Rules and wedges: one SVG, like the live board's overlay.
		const W = s.width * U;
		const H = s.height * U;
		const svg = document.createElementNS(SVG, 'svg');
		svg.setAttribute('class', 'hb-ov');
		svg.setAttribute('viewBox', `0 0 ${W} ${H}`);
		svg.setAttribute('preserveAspectRatio', 'none');
		svg.setAttribute('aria-hidden', 'true');
		let rules = '';
		for (let k = 1; k < s.width; k++) rules += `M${k * U} 0V${H}`;
		for (let k = 1; k < s.height; k++) rules += `M0 ${k * U}H${W}`;
		const rp = document.createElementNS(SVG, 'path');
		rp.setAttribute('class', 'hb-rules');
		rp.setAttribute('d', rules);
		svg.append(rp);
		for (const cl of s.clues) {
			const g = document.createElementNS(SVG, 'g');
			g.setAttribute('transform', `translate(${cl.arrow.x} ${cl.arrow.y})`);
			const p = document.createElementNS(SVG, 'path');
			p.setAttribute('class', 'hb-arw');
			p.setAttribute('d', ARROW_SHAPES[cl.arrow.shape]);
			p.style.transformOrigin = ARROW_ORIGIN[cl.arrow.shape];
			p.style.setProperty('--sd', this.cells[cl.cell]?.style.getPropertyValue('--sd') || '0ms');
			g.append(p);
			svg.append(g);
			this.arrows.set(cl.answerId, p);
		}
		this.fx = el('div', 'hb-fx');
		b.append(grid, svg, this.fx);
	}

	frameW(): number {
		return parseFloat(getComputedStyle(this.el).borderTopWidth) || 3;
	}

	size(cs: number): void {
		this.cs = cs;
		this.el.style.setProperty('--cs', `${cs}px`);
	}

	/** The biggest cell (to a quarter pixel) that fits the board in a w × h box. */
	fitTo(w: number, h: number, max = HERO_CELL_MAX): void {
		const f = this.frameW();
		const side = Math.max(this.s.width, this.s.height);
		this.size(Math.min(max, Math.max(6, Math.floor(((Math.min(w, h) - 2 * f) / side) * 4) / 4)));
	}

	private k(): number {
		return Math.max(1, Math.max(this.s.width, this.s.height) / 8) * 0.9;
	}

	seg(a: [number, number], b: [number, number]): HTMLElement {
		const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
		const ang = Math.atan2(b[1] - a[1], b[0] - a[0]);
		const s = el('i', 'hb-seg');
		s.style.cssText = `left:calc(var(--cs) * ${a[0] / U});top:calc(var(--cs) * ${a[1] / U});width:calc(var(--cs) * ${len / U});--ang:${ang}rad;--k:${this.k()}`;
		this.fx.append(s);
		return s;
	}

	dot(p: [number, number]): HTMLElement {
		const d = el('i', 'hb-dot');
		d.style.cssText = `left:calc(var(--cs) * ${p[0] / U});top:calc(var(--cs) * ${p[1] / U});--k:${this.k()}`;
		this.fx.append(d);
		return d;
	}

	mid(id: string): [number, number] {
		return answerMid(this.s, this.s.answers.find((a) => a.id === id)?.cells ?? []);
	}

	/**
	 * The best Flow chain, drawn the way the poster draws it (BoardMap's `routeStyle`): a
	 * short chain as a route through each answer's midpoint, a long one as the answers
	 * themselves lit along the grid, and a whole-board one lighter, with its start and end
	 * marked and a cobalt keyline round the board. One Flow picture means one thing.
	 */
	route(ids: readonly string[], clock: Clock | null): void {
		const style = routeStyle(ids.length, this.s.answers.length);
		if (style !== 'line') return this.bars(ids, style === 'whole', clock);
		const parts: HTMLElement[] = [];
		const pts = ids.map((id) => this.mid(id));
		pts.forEach((pt, i) => {
			if (i) parts.push(this.seg(pts[i - 1]!, pt));
			parts.push(this.dot(pt));
		});
		if (!clock) parts.forEach((x) => x.classList.add('on'));
		else parts.forEach((x, i) => clock.later(() => x.classList.add('on'), 200 + i * 110));
	}

	/** A long chain: each answer a bar along its own row or column (the poster's `.bmap-bar`). */
	private bars(ids: readonly string[], whole: boolean, clock: Clock | null): void {
		const { width, height } = this.s;
		const W = width * U;
		const H = height * U;
		const svg = document.createElementNS(SVG, 'svg');
		svg.setAttribute('class', whole ? 'hb-route bmap-whole' : 'hb-route');
		svg.setAttribute('viewBox', `0 0 ${W} ${H}`);
		svg.setAttribute('preserveAspectRatio', 'none');
		svg.setAttribute('aria-hidden', 'true');
		svg.style.setProperty('--map-k', String(Math.max(1, Math.max(width, height) / 8)));
		const centre = (i: number): [number, number] => [(i % width) * U + U / 2, Math.floor(i / width) * U + U / 2];
		const segs = ids.flatMap((id) => {
			const cells = this.s.answers.find((a) => a.id === id)?.cells;
			return cells?.length ? [[...centre(cells[0]!), ...centre(cells[cells.length - 1]!)] as [number, number, number, number]] : [];
		});
		if (whole) {
			const key = 9;
			const lit = document.createElementNS(SVG, 'rect');
			lit.setAttribute('class', 'bmap-lit');
			for (const [k, v] of Object.entries({ x: key, y: key, width: W - 2 * key, height: H - 2 * key })) lit.setAttribute(k, String(v));
			svg.append(lit);
		}
		segs.forEach(([x1, y1, x2, y2], i) => {
			const p = document.createElementNS(SVG, 'path');
			p.setAttribute('class', 'bmap-bar');
			p.setAttribute('pathLength', '1');
			p.setAttribute('d', `M${x1} ${y1}L${x2} ${y2}`);
			p.style.setProperty('--k', String(i));
			svg.append(p);
		});
		const first = segs[0];
		const last = segs[segs.length - 1];
		if (whole && first && last) {
			const r = String(Math.round(U * 0.17 * Math.max(1, Math.max(width, height) / 8)));
			for (const [cx, cy, cls, k] of [
				[first[0], first[1], 'bmap-end start', 0],
				[last[2], last[3], 'bmap-end', segs.length],
			] as const) {
				const c = document.createElementNS(SVG, 'circle');
				c.setAttribute('class', cls);
				c.setAttribute('cx', String(cx));
				c.setAttribute('cy', String(cy));
				c.setAttribute('r', r);
				c.style.setProperty('--k', String(k));
				svg.append(c);
			}
		}
		this.fx.append(svg);
		if (clock) {
			svg.style.setProperty('--route-delay', '200ms');
			svg.classList.add('draw');
		}
	}

	/** Select an answer the way the live board does: its clue vermilion, its wedge up, its path awake. */
	select(id: string | null): void {
		for (const x of this.el.querySelectorAll('.hb-cl.on, .hb-arw.on')) x.classList.remove('on');
		for (const x of this.el.querySelectorAll('.hb-cell.path')) x.classList.remove('path');
		if (!id) return;
		this.halves.get(id)?.classList.add('on');
		this.arrows.get(id)?.classList.add('on');
		const cells = this.s.answers.find((a) => a.id === id)?.cells ?? [];
		cells.forEach((i, k) => {
			const e = this.cells[i];
			if (!e) return;
			e.style.setProperty('--k', String(k + 1));
			e.classList.add('path');
		});
	}

	glyph(i: number): HTMLSpanElement | undefined {
		return this.glyphs.get(i);
	}

	isPressed(i: number): boolean {
		return this.pressed.has(i);
	}

	/** Lifts every tried sort and clears the thread (before the board hands over). */
	clear(): void {
		for (const g of this.glyphs.values()) {
			g.className = 'hb-g';
			g.textContent = '';
		}
		this.fx.replaceChildren();
	}
}

/**
 * The attract loop plays this many chains, then rests on the still frame: nothing on
 * Today moves on its own past a few chains (WCAG 2.2.2, docs/ACCESSIBILITY.md M5).
 */
export const ATTRACT_RUNS = 3;

export interface Engine {
	stop(): void;
	/** The answer the ghost is on right now, or null between chains. */
	current(): string | null;
}

/**
 * B · Attract: the board plays itself, like an arcade cabinet. A ghost selection walks a
 * Flow-like chain of crossing answers; in each answer it tries one random sort per cell
 * and lifts it straight out (at most one letter of one answer is ever on the board, so
 * no word can be read). A cobalt thread stitches each answer to the next; then it rests
 * and starts again elsewhere, `ATTRACT_RUNS` times; then it rests on the still frame.
 * `still` (reduced motion): that frame from the start, one answer held selected, nothing
 * moving.
 */
export function attractEngine(view: HeroView, opts: { still: boolean; intro: boolean }): Engine {
	const clock = new Clock();
	const s = view.s;
	const open = (id: string) => s.answers.find((a) => a.id === id)?.cells.filter((i) => !view.isPressed(i)) ?? [];
	const starts = s.answers.filter((a) => a.axis === 'across' && open(a.id).length > 0).map((a) => a.id);
	const pool = starts.length ? starts : s.answers.filter((a) => open(a.id).length > 0).map((a) => a.id);
	let cur: string | null = null;
	let n = 0;
	const big = Math.max(s.width, s.height) > 9;

	const tryAt = (i: number) => {
		const g = view.glyph(i);
		if (!g) return;
		g.className = 'hb-g on';
		g.textContent = randomLetter();
		clock.later(() => (g.textContent = randomLetter(g.textContent ?? undefined)), 70);
		clock.later(() => g.classList.add('lift'), 125);
	};

	const loop = () => {
		view.fx.classList.remove('fade');
		view.fx.replaceChildren();
		const start = pool[(n * 3) % pool.length];
		if (!start) return;
		const ch = crossingChain(s, start, big ? 6 : 5, n + 3).filter((id) => open(id).length > 0);
		n++;
		let t = 0;
		ch.forEach((id, k) => {
			clock.later(() => {
				cur = id;
				view.select(id);
			}, t);
			const cells = open(id);
			cells.forEach((i, j) => clock.later(() => tryAt(i), t + 300 + j * 190));
			t += 300 + cells.length * 190 + 160;
			if (k) {
				const a = view.mid(ch[k - 1]!);
				const b = view.mid(id);
				clock.later(() => view.seg(a, b).classList.add('on'), t - cells.length * 190 - 460);
			}
			clock.later(() => {
				const d = view.dot(view.mid(id));
				clock.frame(() => d.classList.add('on'));
			}, t - 120);
		});
		clock.later(() => {
			view.select(null);
			cur = null;
		}, t + 80);
		clock.later(() => view.fx.classList.add('fade'), t + 1500);
		clock.later(n >= ATTRACT_RUNS ? rest : loop, t + 2600);
	};

	// The still frame: reduced motion's only frame, and where the attract loop comes to rest.
	// One answer held selected, "a puzzle waiting for you", and no Flow thread: on an empty
	// board it would join nothing, and cobalt means Flow everywhere else (QA1-3). The thread
	// is for the runs, where letters flick in along it.
	function rest() {
		view.fx.classList.remove('fade');
		view.fx.replaceChildren();
		const first = pool[0];
		if (first) {
			view.select(first);
			cur = first;
		}
	}

	if (opts.still) rest();
	else if (opts.intro) {
		view.el.classList.add('setting');
		clock.later(() => {
			view.el.classList.remove('setting');
			loop();
		}, 700 + s.width * s.height * 6);
	} else clock.later(loop, 400);

	return {
		stop: () => clock.stop(),
		current: () => cur,
	};
}
